<script setup lang="ts">
import { ref } from "vue"
import PrimaryButton from "./PrimaryButton.vue"

const img = useImage()
const posterUrl = computed(() => img("/images/ferry-poster.webp", { width: 1920, quality: 45, format: "webp" }))
const isVideoPlaying = ref(false)

const videoRef = ref<HTMLVideoElement | null>(null)
const showVideo = ref(false)

useHead({
	link: [
		{
			rel: "preload",
			as: "image",
			href: posterUrl,
			fetchpriority: "high"
		}
	]
})

interface Props {
	title?: string
	excerpt?: string
	videoUrl?: string
	posterUrl?: string
	primaryButtonText?: string
	primaryButtonUrl?: string
	secondaryButtonText?: string
	secondaryButtonUrl?: string
	tertiaryButtonText?: string
	tertiaryButtonUrl?: string
}
defineProps<Props>()

onMounted(() => {
	const start = () => {
		showVideo.value = true
		nextTick(() => {
			videoRef.value?.load()
		})
	}
	if ("requestIdleCallback" in window) {
		requestIdleCallback(start, { timeout: 3000 })
	} else {
		setTimeout(start, 1500)
	}
})
</script>

<template>
	<div class="relative z-20 h-[70vh] overflow-visible sm:h-[90vh]">
		<div class="absolute bottom-0 w-full">
			<div class="button-group bg-palladian text-brand absolute top-0 left-1/2 z-50 flex -translate-x-1/2 -translate-y-1/2 flex-row">
				<span class="button-group__item">
					<PrimaryButton :url="primaryButtonUrl" :event-name="'go_to_meetings_and_events'" :event-params="{ button_location: '/' }" :text="primaryButtonText" class="rounded-r-none border-0 px-6 py-3 text-2xl uppercase hover:cursor-pointer" />
				</span>
				<span class="button-group__item">
					<PrimaryButton :url="secondaryButtonUrl" :event-params="{ button_location: '/' }" :event-name="'go_to_rooftop'" :text="secondaryButtonText" class="rounded-none border-0 px-6 py-3 text-2xl uppercase hover:cursor-pointer" />
				</span>
				<span class="button-group__item">
					<PrimaryButton :url="tertiaryButtonUrl" :event-name="'go_to_apartments'" :event-params="{ button_location: '/' }" :text="tertiaryButtonText" class="rounded-l-none border-0 px-6 py-3 text-2xl uppercase hover:cursor-pointer" />
				</span>
			</div>
		</div>
		<div class="relative flex h-full flex-col">
			<video v-if="videoUrl" ref="videoRef" :poster="posterUrl" autoplay muted playsinline loop preload="none" class="absolute inset-0 z-0 h-full w-full object-cover" @playing="isVideoPlaying = true">
				<source v-if="showVideo" :src="videoUrl" type="video/mp4" />
				Your browser does not support the video tag.
			</video>
			<NuxtImg v-else src="/images/ferry-poster.webp" alt="Hero background poster" sizes="sm:100vw md:100vw lg:100vw" densities="1x" format="webp" quality="45" loading="eager" fetchpriority="high" class="absolute inset-0 z-0 h-full w-full object-cover hue-rotate-15" />
			<div class="absolute inset-0 z-10 bg-black/40"></div>

			<div class="relative z-20 mx-auto flex h-full max-w-6xl flex-col justify-center gap-2 px-8 text-center">
				<h1 v-if="title" class="text-4xl">{{ title }}</h1>
				<p v-if="excerpt" class="mx-auto my-4 max-w-[320px] leading-6 font-light sm:max-w-2xl sm:px-4 sm:text-xl sm:leading-8 md:max-w-3xl md:px-0" v-html="excerpt"></p>
			</div>
		</div>
	</div>
</template>
<style scoped>
.button-group__item {
	position: relative;
	display: flex;
}

.button-group__item:not(:last-child)::after {
	content: "|";
	position: absolute;
	top: 37%;
	right: 0;
	height: 16px;
	transform: translate(50%, -50%);
	color: var(--palladian);
	pointer-events: none;
	z-index: 1;
}
</style>
